import React from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { 
  TrendingUp, Calculator, BarChart3, PieChart, Award, LineChart, 
  ArrowUpDown, Percent, Building2, Wallet, Target, Layers, 
  Scale, Clock, DollarSign, Banknote, ArrowRightLeft, Gift,
  FileText, Search, Landmark, Activity, Gauge
} from "lucide-react";

const RESEARCH_CATEGORIES = [
  {
    title: "Fund Performance & Rankings",
    description: "Analyze fund returns and compare across categories",
    icon: TrendingUp,
    color: "from-blue-500/20 to-blue-600/10",
    tools: [
      { name: "Trailing Returns", path: "/research/trailing-returns", icon: TrendingUp, desc: "1Y, 3Y, 5Y, 10Y returns" },
      { name: "Annual Returns", path: "/research/annual-returns", icon: BarChart3, desc: "Year-wise performance" },
      { name: "Top Funds - AMC Wise", path: "/research/top-funds-amc", icon: Building2, desc: "Best performers by AMC" },
      { name: "Quartile Ranking", path: "/research/quartile-ranking", icon: Award, desc: "Category-wise rankings" },
      { name: "Volatility Ranking", path: "/research/volatility-ranking", icon: Activity, desc: "Risk-adjusted analysis" },
      { name: "Downside Volatility", path: "/research/volatility-ranking", icon: ArrowUpDown, desc: "Downside risk metrics" },
      { name: "Category Monitor", path: "/research/trailing-returns", icon: PieChart, desc: "Category-level trends" },
      { name: "Benchmark Monitor", path: "/research/trailing-returns", icon: Target, desc: "Index comparisons" },
      { name: "PPF vs ELSS", path: "/research/ppf-vs-elss", icon: Scale, desc: "Tax saving comparison" },
      { name: "Benchmark Returns", path: "/research/trailing-returns", icon: LineChart, desc: "Index returns data" },
    ]
  },
  {
    title: "SIP & STP Calculators",
    description: "Plan your systematic investments with precision",
    icon: Calculator,
    color: "from-green-500/20 to-green-600/10",
    tools: [
      { name: "SIP Returns", path: "/research/sip-calculator", icon: Calculator, desc: "Fund-wise SIP returns" },
      { name: "ELSS SIP Returns", path: "/research/sip-calculator", icon: Percent, desc: "Tax-saver SIP analysis" },
      { name: "SIP Calculator", path: "/research/sip-calculator", icon: Calculator, desc: "Plan your SIP" },
      { name: "SIP Calculator - AMC Wise", path: "/research/top-funds-amc", icon: Building2, desc: "AMC-specific SIP" },
      { name: "Step-up SIP", path: "/research/sip-calculator", icon: TrendingUp, desc: "Annual increase SIP" },
      { name: "STP Calculator", path: "/research/stp-calculator", icon: ArrowRightLeft, desc: "Systematic transfer" },
      { name: "STP Profit Transfer", path: "/research/stp-calculator", icon: DollarSign, desc: "Profit booking STP" },
      { name: "Step-up STP", path: "/research/stp-calculator", icon: TrendingUp, desc: "STP with increase" },
      { name: "Top SWP Funds", path: "/research/swp-calculator", icon: Wallet, desc: "Best for withdrawals" },
      { name: "SWP Calculator", path: "/research/swp-calculator", icon: Calculator, desc: "Plan withdrawals" },
    ]
  },
  {
    title: "Returns Analysis",
    description: "Lumpsum and rolling returns analysis",
    icon: BarChart3,
    color: "from-purple-500/20 to-purple-600/10",
    tools: [
      { name: "Lumpsum Returns", path: "/research/lumpsum-calculator", icon: Banknote, desc: "One-time investment" },
      { name: "Lumpsum Calculator", path: "/research/lumpsum-calculator", icon: Calculator, desc: "Plan lumpsum" },
      { name: "Rolling vs Category", path: "/consistent-performers", icon: LineChart, desc: "Consistency check" },
      { name: "Rolling vs Benchmark", path: "/consistent-performers", icon: Target, desc: "Index comparison" },
      { name: "Rolling vs Other Index", path: "/consistent-performers", icon: Activity, desc: "Multi-index analysis" },
      { name: "Rolling vs Sensex & Gold", path: "/consistent-performers", icon: TrendingUp, desc: "Asset comparison" },
      { name: "Liquid vs Savings Bank", path: "/research/liquid-vs-savings", icon: Landmark, desc: "Cash alternatives" },
      { name: "Category Returns", path: "/research/trailing-returns", icon: PieChart, desc: "Category averages" },
      { name: "Market Capture Ratio", path: "/research/volatility-ranking", icon: Gauge, desc: "Upside/downside capture" },
      { name: "Latest NAV", path: "/research", icon: Clock, desc: "Real-time NAV data" },
    ]
  },
  {
    title: "Fund Information & Dividends",
    description: "Detailed fund info and dividend analysis",
    icon: FileText,
    color: "from-amber-500/20 to-amber-600/10",
    tools: [
      { name: "Fund Information", path: "/research", icon: FileText, desc: "Complete fund details" },
      { name: "Fund Selector", path: "/screener", icon: Search, desc: "Find the right fund" },
      { name: "Portfolio Overlap", path: "/research/portfolio-overlap", icon: Layers, desc: "Holdings comparison" },
      { name: "Highest Dividend Funds", path: "/research/highest-dividend", icon: Gift, desc: "Top dividend payers" },
      { name: "Consistent Dividends", path: "/research/highest-dividend", icon: Award, desc: "Regular payouts" },
      { name: "Category Dividends", path: "/research/highest-dividend", icon: PieChart, desc: "Category-wise IDCW" },
      { name: "Dividend Comparison", path: "/compare", icon: Scale, desc: "Compare schemes" },
      { name: "Historical Dividends", path: "/research/highest-dividend", icon: Clock, desc: "Past payouts" },
      { name: "AMC Wise Dividends", path: "/research/top-funds-amc", icon: Building2, desc: "AMC dividend data" },
    ]
  }
];

export const ResearchToolsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              MF Research <span className="gold-text">Tools</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              40+ powerful tools to analyze mutual funds, calculate returns, and make informed investment decisions
            </p>
          </div>

          {/* Quick Access */}
          <div className="mb-16">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              Popular Tools
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {[
                { name: "SIP Calculator", path: "/research/sip-calculator", icon: Calculator },
                { name: "Lumpsum Calculator", path: "/research/lumpsum-calculator", icon: Banknote },
                { name: "Top Funds", path: "/top-performers", icon: TrendingUp },
                { name: "Fund Screener", path: "/screener", icon: Search },
                { name: "Compare Funds", path: "/compare", icon: Scale },
                { name: "SWP Calculator", path: "/research/swp-calculator", icon: Wallet },
              ].map((tool) => {
                const Icon = tool.icon;
                return (
                  <Link
                    key={tool.path}
                    to={tool.path}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-card border border-border hover:border-primary/50 hover:bg-primary/5 transition-all group"
                    data-testid={`quick-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-center">{tool.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-12">
            {RESEARCH_CATEGORIES.map((category, idx) => {
              const CategoryIcon = category.icon;
              return (
                <div key={idx} className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                      <CategoryIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">{category.title}</h2>
                      <p className="text-sm text-muted-foreground">{category.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {category.tools.map((tool) => {
                      const ToolIcon = tool.icon;
                      return (
                        <Link
                          key={tool.path}
                          to={tool.path}
                          className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all group"
                          data-testid={`tool-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0 group-hover:bg-primary/10 transition-colors">
                            <ToolIcon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-medium text-sm group-hover:text-primary transition-colors">{tool.name}</h3>
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{tool.desc}</p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
